import moment from 'moment';
import { newServices } from '../../_services/newservices.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { bookingService } from '../../_services/booking.service';

/**
 * First-run starter setup (ONBOARDING-FIRST-RUN-PLAN.md, Steps B and D).
 *
 * Writes through the same endpoints the catalogue, Clients and the diary use,
 * so everything it creates is ordinary data the owner can edit or delete:
 *   1. categories and services from the ticked starter rows, offered by every
 *      team member and bookable online;
 *   2. one sample client with no phone or email, so nothing can ever be sent;
 *   3. one practice booking about an hour from now, in the column of the first
 *      self-employed stylist when there is one (so checkout shows the split
 *      card), otherwise the owner's.
 *
 * Safe to run twice: services whose names already exist are skipped, and the
 * sample client and practice booking are reused when their ids are known.
 */

export const SAMPLE_CLIENT = { first_name: 'Sample', last_name: 'Client (practice)' };
export const SAMPLE_CLIENT_NAME = `${SAMPLE_CLIENT.first_name} ${SAMPLE_CLIENT.last_name}`;

const ok = (res) => res && res.data && res.data.statusCode === 1;
const result = (res) => (res && res.data && res.data.responseData && res.data.responseData.result) || null;
const norm = (s) => String(s || '').trim().toLowerCase();

async function existingCatalogue() {
  const [cats, svcs] = await Promise.all([newServices.listCategory({}), newServices.listServices({})]);
  const categoryIds = {};
  (result(cats) || []).forEach((c) => { categoryIds[norm(c.categoryName)] = c._id; });
  const serviceNames = new Set();
  (result(svcs) || []).forEach((c) => (c.serviceList || []).forEach((s) => serviceNames.add(norm(s.serviceName))));
  return { categoryIds, serviceNames };
}

async function teamIds() {
  const res = await newServices.listHairdresser({});
  const list = result(res) || [];
  const ids = list.map((h) => h._id).filter(Boolean);
  const me = localStorage.getItem('userId');
  if (me && !ids.includes(me)) ids.unshift(me);
  return ids;
}

/** Creates the ticked starter rows. Returns how many services were added. */
export async function createStarterServices(rows, onProgress) {
  const picked = rows.filter((r) => r.on && r.name.trim());
  if (!picked.length) return { created: 0, skipped: 0 };
  const [{ categoryIds, serviceNames }, staff] = await Promise.all([existingCatalogue(), teamIds()]);

  let created = 0;
  let skipped = 0;
  for (let i = 0; i < picked.length; i += 1) {
    const row = picked[i];
    if (onProgress) onProgress(i, picked.length);
    if (serviceNames.has(norm(row.name))) { skipped += 1; continue; } // eslint-disable-line no-continue

    let categoryID = categoryIds[norm(row.category)];
    if (!categoryID) {
      // eslint-disable-next-line no-await-in-loop
      const catRes = await newServices.createCategory({ categoryName: row.category });
      if (!ok(catRes)) throw new Error((catRes && catRes.data && catRes.data.error && catRes.data.error.responseMessage) || `Could not add the ${row.category} category.`);
      categoryID = result(catRes) && result(catRes)._id;
      categoryIds[norm(row.category)] = categoryID;
    }

    const payload = {
      serviceName: row.name.trim(),
      categoryID,
      serviceDescription: '',
      diaryColour: '#c8eeee',
      hairdresserService: staff.map((hairdresserId) => ({
        hairdresserId,
        servicePrice: parseFloat(row.price) || 0,
        serviceTime: parseInt(row.time, 10) || 30,
        isDeactivated: false,
        depositPrice: 0,
        onlineBooking: true,
        processingTime: 0,
        setupTime: 0,
        cleanupTime: 0,
        requiresFinishing: false,
        finishingServiceIds: [],
        finishingSameStylist: true,
        requiresConsultation: false,
        consultationServiceId: null,
        consultationSameStylist: true,
        finishingMessage: '',
        consultationMessage: '',
        diaryColour: '#c8eeee',
        isColourOverridden: false,
        linkedForms: [],
        resourceRequirements: [],
      })),
    };
    // eslint-disable-next-line no-await-in-loop
    const svcRes = await newServices.createServices(payload);
    if (!ok(svcRes)) throw new Error((svcRes && svcRes.data && svcRes.data.error && svcRes.data.error.responseMessage) || `Could not add ${row.name}.`);
    serviceNames.add(norm(row.name));
    created += 1;
  }
  if (onProgress) onProgress(picked.length, picked.length);
  return { created, skipped };
}

/** The sample client. No phone, no email, no marketing consent. */
export async function createSampleClient() {
  const res = await hairdresserService.clientAdd({
    hairdresserId: localStorage.getItem('userId'),
    name: SAMPLE_CLIENT_NAME,
    first_name: SAMPLE_CLIENT.first_name,
    last_name: SAMPLE_CLIENT.last_name,
    email: '',
    phoneNumber: '',
    note: 'Practice client from setup. Safe to delete.',
    dob: '',
    emailMarketingConsent: false,
    smsMarketingConsent: false,
  });
  const data = (res && res.data && (res.data.responseData || res.data.data)) || {};
  const client = data.client || data.result || data.existingClient || data;
  const id = client && (client._id || client.id || client.userId);
  if (!id) throw new Error('Could not add the sample client.');
  return id;
}

/** First self-employed stylist in the salon, or null. */
export async function firstSelfEmployedStylist() {
  try {
    const res = await hairdresserService.staffListServiceV2WithoutService({ hairdresserId: localStorage.getItem('userId'), page: 1, listType: 1 });
    const list = (res && res.data && res.data.data && res.data.data.total) || [];
    const se = list.find((s) => s.hairdresserInfo && s.hairdresserInfo.employmentTypeId === 2 && s.hairdresserInfo.isSalonOwner !== 1);
    return se ? se._id : null;
  } catch (e) {
    return null;
  }
}

/** Next quarter hour at least an hour away, inside a sensible working day. */
export function practiceSlot(now = moment()) {
  const t = now.clone().add(60, 'minutes');
  const rem = t.minute() % 15;
  if (rem) t.add(15 - rem, 'minutes');
  t.seconds(0);
  let day = t.clone().startOf('day');
  let minutes = t.hours() * 60 + t.minutes();
  if (minutes < 9 * 60) minutes = 10 * 60;
  if (minutes > 20 * 60) { day = day.add(1, 'day'); minutes = 10 * 60; }
  return { date: day.format('YYYY-MM-DD'), startMinutes: minutes };
}

/**
 * One practice booking for the sample client. Prefers a starter service the
 * stylist offers; falls back to their first service of any kind.
 */
export async function createPracticeBooking({ clientId, preferNames = [] }) {
  const staffId = (await firstSelfEmployedStylist()) || localStorage.getItem('userId');
  const svcRes = await hairdresserService.getServicesGroupedV2({ hairdresserId: staffId });
  const grouped = (svcRes && svcRes.data && svcRes.data.data && svcRes.data.data.hairdresserSelectedServices) || [];
  const all = grouped.reduce((acc, g) => acc.concat(g.services || []), []);
  if (!all.length) throw new Error('No services to book yet.');
  const wanted = new Set(preferNames.map(norm));
  const svc = all.find((s) => wanted.has(norm(s.serviceName))) || all[0];

  const time = parseInt(svc.serviceTime, 10) || 30;
  const { date, startMinutes } = practiceSlot();
  const id = svc._id || svc.serviceId;
  const res = await bookingService.createBookingV2({
    servicesToBook: [{
      service: { serviceId: id, _id: id, serviceName: svc.serviceName, servicePrice: svc.servicePrice },
      id,
      _id: id,
      serviceId: id,
      serviceName: svc.serviceName,
      servicePrice: svc.servicePrice,
      price: parseFloat(svc.servicePrice || 0),
      serviceTime: time,
      processingTime: 0,
      setupTime: 0,
      cleanupTime: 0,
      hairdresserId: staffId,
    }],
    bookingSlot: [startMinutes, startMinutes + time],
    hairdresserId: staffId,
    bookingDate: date,
    clientId,
    salonId: localStorage.getItem('salonId'),
    totalAmount: parseFloat(svc.servicePrice || 0),
    invoiceNumber: localStorage.getItem('invoiceNumber'),
    bookingNote: 'Practice booking from setup',
    transaction: [],
  });
  if (!ok(res)) throw new Error((res && res.data && res.data.error && res.data.error.message) || 'Could not add the practice booking.');
  const bookingId = result(res) && result(res)._id;
  return { bookingId, staffId, date };
}

/**
 * Go-live tidy up: cancel the practice booking and remove the sample client.
 * If the practice booking went through checkout both stay, because payments
 * cannot be deleted from the front end and a paid booking should keep its
 * client. Never throws.
 */
export async function removePracticeData(practice) {
  const out = { client: false, booking: false };
  if (!practice) return out;
  if (practice.bookingId && !practice.paid) {
    try {
      const res = await bookingService.hairdresserCancelBooking({ hairdresserId: practice.staffId || localStorage.getItem('userId'), bookingID: practice.bookingId, refundDeposit: 0, deviceToken: 'onboarding' });
      // v2 refusals come back as HTTP 200 with statusCode 0.
      out.booking = !!(res && res.data) && !(res.data.errorCode || res.data.statusCode === 0 || res.data.error);
    } catch (e) { /* leave it */ }
  }
  if (practice.clientId && !practice.paid) {
    try {
      const res = await hairdresserService.deleteClient({ userId: practice.clientId });
      out.client = !!(res && res.data) && !(res.data.errorCode || res.data.statusCode === 0 || res.data.error);
    } catch (e) { /* leave it */ }
  }
  return out;
}
